import { Close as CloseIcon, Clear as ClearIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Grow,
  InputAdornment,
  ListItem,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import InlineSearch from "components/common/InlineSearch";
import * as labels from "config.labels";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import * as claimsService from "services/claimsService";
import * as submissionsService from "services/submissionsService";
import * as peersActions from "store/actions/peers/peersActions";
import * as searchActions from "store/actions/search/searchActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as peersSelectors from "store/selectors/peers/peersSelectors";
import * as utils from "utils";

const PeerSelectionTable = ({
  query,
  builtQuery,
  loading,
  peers,
  submissions,
  addPeer,
  height = 400,
}) => {
  const existingPeerFilenames = peers.map((p) => p.filename);
  const availablePeers = submissions.filter(
    (s) => !existingPeerFilenames.includes(s.filename)
  );
  return (
    <div
      style={{
        width: "auto",
        height,
        justifyItems: "center",
        alignContent: availablePeers.length > 0 ? "flex-start" : "center",
        backgroundColor: "rgba(127, 127, 127, 0.05)",
        borderRadius: 5,
      }}
    >
      {availablePeers.length > 0 ? (
        <TableContainer style={{ maxHeight: height - 4 }}>
          <Table stickyHeader className={"peers-table"}>
            <TableBody>
              {availablePeers.map((submission) => (
                <TableRow
                  key={submission.submissionId}
                  xs={12}
                  className={"search-row"}
                  onClick={() => addPeer(submission)}
                >
                  <TableCell className={"search-cell search-result-insured"}>
                    <span>{submission.insured}</span>
                  </TableCell>
                  <TableCell className={"search-cell search-result-inception"}>
                    <span>{utils.formatDate(submission.inception)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : loading ? (
        <Stack sx={{ width: "100%" }} alignItems={"center"}>
          <DefaultLoader size={24} />
        </Stack>
      ) : (
        <Typography textAlign={"center"} color={"lightslategrey"}>
          {submissions != null && builtQuery != null && builtQuery?.length > 0
            ? query?.length > 0
              ? `No matches found for '${query}'`
              : "No matches found"
            : "Peers matching your search will appear here"}
        </Typography>
      )}
    </div>
  );
};

const PeersPills = ({ peers, deletePeer }) => {
  const theme = useTheme();
  const sx = {
    color: theme.brand.white,
    ".MuiChip-deleteIcon": {
      color: theme.brand.white,
      opacity: 0.35,
      "&:hover": {
        color: theme.brand.white,
        opacity: 1,
      },
    },
  };

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      flexWrap={"wrap"}
      marginBottom={2}
      gap={1}
    >
      {(peers ?? []).map((peer, index) => (
        <Chip
          key={index}
          label={peer.insured || "Untitled"}
          color={"primary"}
          sx={sx}
          onDelete={index === 0 ? null : () => deletePeer(peer?.filename)}
        />
      ))}
    </Stack>
  );
};

const PeerSelector = ({ peers, addPeer, query, cacheKey }) => {
  const builtQuery = makeQuery({ query });
  const {
    data: submissions,
    isFetching: loading,
  } = submissionsService.useQuerySubmissionsQuery(
    {
      cacheKey,
      query: builtQuery,
      sort: "-savedAt",
      limit: 30,
    },
    { skip: !cacheKey || !builtQuery }
  );

  return (
    <Stack direction={"column"} spacing={1} justifyContent={"center"}>
      <Typography variant={"h6"}>{"Peers"}</Typography>
      <PeerSelectionTable
        submissions={submissions ?? []}
        peers={peers ?? []}
        addPeer={addPeer}
        query={query}
        loading={loading}
      />
    </Stack>
  );
};

const IndustryClassPeerSelector = ({
  peers,
  addPeer,
  query,
  industryClasses,
  submissionIndustryClass,
  cacheKey,
}) => {
  const theme = useTheme();
  const [industryClass, setIndustryClass] = useState("SAME");
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const builtQuery = makeQuery({
    query,
    industryClass:
      industryClass === "SAME"
        ? submissionIndustryClass
        : industryClass === "ANY"
        ? false
        : industryClass,
  });
  const {
    data: submissions,
    isFetching: loading,
  } = submissionsService.useQuerySubmissionsQuery(
    {
      cacheKey,
      query: builtQuery,
      sort: "-savedAt",
      limit: 30,
    },
    { skip: !cacheKey || !builtQuery }
  );

  return (
    <Stack direction={"column"} spacing={1} justifyContent={"center"}>
      <Autocomplete
        disablePortal
        options={[
          { name: "Any Industry", key: "ANY" },
          { key: "DIVIDER" },
          { name: "Same as Submission", key: "SAME" },
          { key: "DIVIDER" },
          ...industryClasses,
        ]}
        defaultValue={{ name: "Same as Submission", key: "SAME" }}
        onChange={(_, value) => setIndustryClass(value?.key ?? "ANY")}
        getOptionLabel={(option) => option.name ?? ""}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        renderInput={(params) => (
          <TextField
            {...params}
            size={"small"}
            variant={"outlined"}
            sx={{ background: theme.brand.white }}
            label={translateLabel(labels.INDUSTRY_CLASS_PEERS_INPUT_LABEL, {
              default: "Industry Class Peers",
            })}
          />
        )}
        renderOption={(props, option) => {
          return option.key === "DIVIDER" ? (
            <Divider sx={{ margin: "0.5rem 0" }} />
          ) : (
            <ListItem {...props}>{option.name}</ListItem>
          );
        }}
      />
      <PeerSelectionTable
        submissions={submissions ?? []}
        peers={peers ?? []}
        addPeer={addPeer}
        query={query}
        builtQuery={builtQuery}
        loading={loading}
      />
    </Stack>
  );
};

const PeerNAICSSearch = ({ label, setCode, naics }) => {
  const theme = useTheme();
  const textInputRef = useRef(null);
  const [query, setQuery] = useState("Same as Submission");
  const [selecting, setSelecting] = useState(false);
  const { data: codes, isFetching: loading } = claimsService.useNAICSCodesQuery(
    {
      query,
    },
    {
      skip: !selecting,
    }
  );

  const open = (codes?.length || []) > 0 && selecting && !loading;

  return (
    <ClickAwayListener onClickAway={() => setSelecting(false)}>
      <div className={"search-text-field"}>
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position={"end"}>
                {loading ? (
                  <DefaultLoader size={12} style={{ marginRight: "14px" }} />
                ) : (
                  <IconButton
                    icon={ClearIcon}
                    onClick={() => {
                      setQuery("");
                      setCode(null);
                      setSelecting(false);
                    }}
                  />
                )}
              </InputAdornment>
            ),
          }}
          variant={"outlined"}
          size={"small"}
          autoComplete={"off"}
          label={label}
          fullWidth
          onChange={(e) => {
            setQuery(e.target.value);
            setSelecting(true);
            setCode(null);
          }}
          value={query}
          inputRef={textInputRef}
          sx={{ background: theme.brand.white }}
        />
        <Popper
          open={open}
          role={undefined}
          transition
          placement={"bottom-start"}
          disablePortal
          className={"search-text-field-results"}
          style={{
            position: "absolute",
            zIndex: 99,
            top: 56,
            minWidth: "100%",
          }}
          anchorEl={textInputRef.current}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <MenuList className={"result-list"}>
                  <MenuItem
                    onClick={() => {
                      setSelecting(false);
                      setCode(naics?.code);
                      setQuery("Same as Submission");
                    }}
                    divider
                    key={`NAICS_CODE_SEARCH_SAME_AS_SUBMISSION`}
                  >
                    {"Same as Submission"}
                  </MenuItem>
                  {codes.map((code, id) => (
                    <MenuItem
                      onClick={() => {
                        setSelecting(false);
                        setCode(code._CODE);
                        setQuery(`(${code._CODE}) - ${code._TITLE}`);
                      }}
                      key={`NAICS_CODE_SEARCH_${id}`}
                    >
                      {`(${code._CODE}) - ${code._TITLE}`}
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const NAICSCodePeerSelector = ({ peers, addPeer, naics, query, cacheKey }) => {
  const [code, setCode] = useState(naics?.code);
  const builtQuery = makeQuery({ query, naicsCode: code || false });
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const {
    data: submissions,
    isFetching: loading,
  } = submissionsService.useQuerySubmissionsQuery(
    {
      cacheKey,
      query: builtQuery,
      sort: "-savedAt",
      limit: 30,
    },
    { skip: !cacheKey || !builtQuery }
  );

  return (
    <Stack direction={"column"} spacing={1} justifyContent={"center"}>
      <PeerNAICSSearch
        label={translateLabel(labels.NAICS_PEERS_SEARCH_LABEL, {
          default: "NAICS Peers",
        })}
        setCode={setCode}
        naics={naics}
      />
      <PeerSelectionTable
        submissions={submissions ?? []}
        peers={peers ?? []}
        addPeer={addPeer}
        query={query}
        builtQuery={builtQuery}
        loading={loading}
      />
    </Stack>
  );
};

const makeQuery = ({ query, industryClass, naicsCode }) => {
  let queryBuilder = "";
  if (query) {
    queryBuilder += query;
  }
  if (industryClass) {
    queryBuilder += ` industryClass="${industryClass}"`;
  }
  if (naicsCode) {
    queryBuilder += ` naicsCode>"${naicsCode}"`;
  }
  if (queryBuilder.length < 1) {
    return " ";
  }
  return queryBuilder;
};

const PeerSelectorDialog = ({
  peers,
  addPeer,
  deletePeer,
  industryClasses,
  submissionIndustryClass,
  naics,
  onClose,
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState();
  const [cacheKey, setCacheKey] = useState(null);

  useEffect(() => {
    if (cacheKey == null) {
      const timestamp = new Date().toISOString();
      setCacheKey(timestamp);
    }
  }, [cacheKey, setCacheKey]);

  return (
    <Dialog open={true} maxWidth={"lg"} fullWidth>
      <DialogContent>
        <Stack
          alignItems={"center"}
          direction={"row"}
          justifyContent={"space-between"}
          gap={1}
          marginBottom={2}
        >
          <InlineSearch
            placeholder={"Search peers "}
            onChange={setQuery}
            noBackground
            sx={{ background: `${theme.brand.white} !important` }}
            fullWidth
          />
          <IconButton icon={CloseIcon} onClick={() => onClose()} />
        </Stack>
        <Stack gap={1}>
          <PeersPills peers={peers} deletePeer={deletePeer} />
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            spacing={1}
          >
            <Grid item xs={4}>
              <PeerSelector
                peers={peers}
                addPeer={addPeer}
                query={query}
                cacheKey={cacheKey}
              />
            </Grid>
            <Grid item xs={4}>
              <IndustryClassPeerSelector
                peers={peers}
                addPeer={addPeer}
                query={query}
                cacheKey={cacheKey}
                industryClasses={industryClasses}
                submissionIndustryClass={submissionIndustryClass}
              />
            </Grid>
            <Grid item xs={4}>
              <NAICSCodePeerSelector
                peers={peers}
                naics={naics}
                addPeer={addPeer}
                query={query}
                cacheKey={cacheKey}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          padding: "0 24px 18px 24px",
        }}
      ></DialogActions>
    </Dialog>
  );
};
const mapStateToProps = (state) => {
  return {
    peers: peersSelectors.getPeers(state),
    naics: state?.input?.program?.naics ?? {},
    industryClasses: state.temp.exposureMetrics?.industryClassChoices || [],
    submissionIndustryClass: state?.input?.program?.industryClass ?? null,
  };
};

const mapDispatchToProps = {
  updateSearch: searchActions.updateSearch,
  updateIndustryClass: peersActions.updateIndustryClass,
  addPeer: peersActions.loadPeer,
  deletePeer: peersActions.deletePeer,
};

export default connect(mapStateToProps, mapDispatchToProps)(PeerSelectorDialog);
